// @file Composable for reading clipboard items with error handling and snackbar feedback
import { __ } from '@@/bits/intl'

function useClipboardItems(): { readClipboardItems: () => Promise<ClipboardItems | null> } {
  async function readClipboardItems(): Promise<ClipboardItems | null> {
    try {
      if (typeof navigator.clipboard?.read !== 'function') {
        throw new Error('Clipboard API not supported')
      }
      return await navigator.clipboard.read()
    } catch (error: unknown) {
      if (!(error instanceof Error)) {
        return null
      }

      const [{ useGlobalSnackbarStore }, { SnackbarNotificationType }] = await Promise.all([
        import('@@/pinia/global_snackbar'),
        import('@@/enums'),
      ])
      const globalSnackbarStore = useGlobalSnackbarStore()

      // Error case for permission denied
      // Note: error.messages are localised so we have to use `error.name`
      if (error.name === 'NotAllowedError') {
        globalSnackbarStore.setSnackbar({
          message: __('Clipboard access denied—please enable clipboard permissions in your browser to allow pasting.'),
          notificationType: SnackbarNotificationType.error,
          timeout: 3000,
        })
      } else if (error.name === 'Error' && error.message === 'Clipboard API not supported') {
        // As "the browser not having support for clipboard" does not have an actual error name, we use the "Clipboard API not supported" message above as the identifier
        globalSnackbarStore.setSnackbar({
          message: __('Pasting from the clipboard is not supported in your browser.'),
          notificationType: SnackbarNotificationType.error,
          timeout: 3000,
        })
      } else {
        // Generic error , just in case
        globalSnackbarStore.setSnackbar({
          message: __('Unable to access clipboard.'),
          notificationType: SnackbarNotificationType.error,
          timeout: 3000,
        })
      }

      return null
    }
  }

  return { readClipboardItems }
}

export { useClipboardItems }
